import React from 'react'
import PropTypes from 'prop-types'
import { Link, useHistory } from 'react-router-dom'
import { Check } from 'react-feather'
import { Button } from 'reactstrap'

/**
 * OutreachAvailabilityBadge
 * Renders the availability badge according to design system specifications:
 * - spots >= 20 or unlimited  -> Green "Open" badge (#EAF3DE / #27500A / dot #639922)
 * - spots 1-19               -> Amber "{n} spots left" badge (#FAEEDA / #633806 / dot #EF9F27)
 * - spots = 0 / is_full      -> Red "Full" badge (#FCEBEB / #791F1F / dot #E24B4A)
 */
export const OutreachAvailabilityBadge = ({ isFull, spotsLeft }) => {
    let text = 'Open'
    let bg = '#EAF3DE'
    let textColor = '#27500A'
    let dotColor = '#639922'

    if (isFull || spotsLeft === 0) {
        text = 'Full'
        bg = '#FCEBEB'
        textColor = '#791F1F'
        dotColor = '#E24B4A'
    } else if (spotsLeft !== null && spotsLeft !== undefined && spotsLeft > 0 && spotsLeft < 20) {
        text = `${spotsLeft} ${spotsLeft === 1 ? 'spot' : 'spots'} left`
        bg = '#FAEEDA'
        textColor = '#633806'
        dotColor = '#EF9F27'
    }

    return (
        <div
            style={{
                position: 'absolute',
                top: '12px',
                right: '12px',
                zIndex: 2,
                backgroundColor: bg,
                color: textColor,
                borderRadius: '9999px',
                padding: '4px 12px',
                fontSize: '12px',
                fontWeight: 600,
                display: 'inline-flex',
                alignItems: 'center',
                lineHeight: 1
            }}
        >
            <span
                style={{
                    width: '6px',
                    height: '6px',
                    borderRadius: '50%',
                    backgroundColor: dotColor,
                    marginRight: '6px',
                    display: 'inline-block'
                }}
            />
            {text}
        </div>
    )
}

OutreachAvailabilityBadge.propTypes = {
    isFull: PropTypes.bool,
    spotsLeft: PropTypes.number
}

/**
 * OutreachActionButton
 * Renders CTA buttons per specs:
 * - Applied: "✓ Applied" (bg #EAF3DE, text #27500A)
 * - Available + Not Applied: "Apply" (bg #333247, text #FFFFFF)
 * - Full + Not Applied: "Full" (bg #F1EFE8, text #B4B2A9, disabled)
 */
export const OutreachActionButton = ({ campaign, applyPath }) => {
    const isApplied = Boolean(campaign?.is_applied)
    const isFull = Boolean(campaign?.is_full) || campaign?.spots_left === 0
    const canApply = campaign?.can_apply !== false

    if (isApplied) {
        return (
            <Button
                disabled
                color="none"
                className="w-100 outreach-btn-applied"
            >
                Applied <Check size={16} strokeWidth={2.5} />
            </Button>
        )
    }

    if (!isFull && canApply) {
        const targetPath = applyPath || (campaign?.version === 'v2' ? `/rd/apply-on-campaign/${campaign?.id}` : `/rd/apply-on-campaign/${campaign?.id}`)
        return (
            <Button
                tag={Link}
                to={targetPath}
                color="none"
                className="w-100 outreach-btn-apply"
            >
                Apply
            </Button>
        )
    }

    return (
        <Button
            disabled
            color="none"
            className="w-100 outreach-btn-full"
        >
            Full
        </Button>
    )
}

OutreachActionButton.propTypes = {
    campaign: PropTypes.object.isRequired,
    onViewApplied: PropTypes.func,
    applyPath: PropTypes.string
}
